function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {try {var info = gen[key](arg);var value = info.value;} catch (error) {reject(error);return;}if (info.done) {resolve(value);} else {Promise.resolve(value).then(_next, _throw);}}function _asyncToGenerator(fn) {return function () {var self = this,args = arguments;return new Promise(function (resolve, reject) {var gen = fn.apply(self, args);function _next(value) {asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);}function _throw(err) {asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);}_next(undefined);});};}function _classCallCheck(instance, Constructor) {if (!(instance instanceof Constructor)) {throw new TypeError("Cannot call a class as a function");}}function _defineProperties(target, props) {for (var i = 0; i < props.length; i++) {var descriptor = props[i];descriptor.enumerable = descriptor.enumerable || false;descriptor.configurable = true;if ("value" in descriptor) descriptor.writable = true;Object.defineProperty(target, descriptor.key, descriptor);}}function _createClass(Constructor, protoProps, staticProps) {if (protoProps) _defineProperties(Constructor.prototype, protoProps);if (staticProps) _defineProperties(Constructor, staticProps);return Constructor;}import { getParameterByName } from "../globals";
import { isMobileDevice } from "../compat";
import { DynamicGlobalOffset } from "./DynamicGlobalOffset";

var av = Autodesk.Viewing;
var avp = av.Private;

// Enum for all extension names we are using here
var ExtNames = {
  BimWalk: 'Autodesk.BimWalk',
  Bookmarks: 'Autodesk.AEC.CanvasBookmarkExtension',
  Levels: 'Autodesk.AEC.LevelsExtension',
  CrossFade: 'Autodesk.CrossFadeEffects',
  Hyperlinks: 'Autodesk.AEC.HyperlinkExtension',
  Minimap: 'Autodesk.AEC.Minimap3DExtension',
  DropMe: 'Autodesk.AEC.DropMeExtension',
  ZoomWindow: 'Autodesk.Viewing.ZoomWindow',
  FusionOrbit: 'Autodesk.Viewing.FusionOrbit',
  DiffTool: 'Autodesk.DiffTool',
  ModelStructure: 'Autodesk.ModelStructure' };


// Alignment Services:
// The client can choose the backend that is used to load/save alignment transforms.
//
// Example 1: Use a Default Service
//  options.createModelAlignmentService = av.AggregatedView.AlignmentServices.LocalStorage;
// 
// Example 2: Implement your own
//  options.createModelAlignmentService = () => new MyOwnAlignmentService();
//
// where cfg is options.modelAlignmentConfig in your AggregatedView options.
//
// Note: You can assume the ModelAlignmentExtension code to be available when the callback
//       is executed, but not earlier. 
var AlignmentServices = {
  LocalStorage: function LocalStorage() {return new Autodesk.ModelAlignmentService.AlignmentServiceLS();} };


// Get model key. Input may be bubbleNode, Model, or model key already.
var makeKey = function makeKey(value) {

  if (value instanceof av.BubbleNode) {
    return value.getModelKey();
  }

  // For LMV models, get bubbleNode first
  if (value instanceof av.Model) {var _value$getDocumentNod;
    return (_value$getDocumentNod = value.getDocumentNode()) === null || _value$getDocumentNod === void 0 ? void 0 : _value$getDocumentNod.getModelKey();
  }

  if (typeof value === 'string') {
    return value;
  }
  console.error("makeKey: Input must be key, model, or BubbleNode");
};

var createModelItem = function createModelItem(node) {
  return {
    // av.Model or null (if the model root is loading)
    model: null, // av.Model

    // Same as model.getDocumentNode(), but model may be null if the root is not loaded yet.
    node: node, // av.BubbleNode

    // A model is "set to be visible" if either
    //  a) model is already displayed
    //  b) model will be displayed as soon as model root is loaded
    visible: false,

    // Url used for the loadModel - needed for cancelling loads if model root is not available yet
    url: null,

    // Indicates that the model root of this model is currently loading
    loading: false,

    // Indicates that we already tried loading this model, but failed
    error: false };

};

// Empty 3D views may be confusing and easily mistaken for a bug.
// Therefore, we notify the user if the shown model is empty.
var warnIfModelEmpty = function warnIfModelEmpty(model) {
  var data = model.getData();
  var bubbleNode = data.loadOptions.bubbleNode;
  var modelEmpty = model.is3d() && data.metadata.stats && !data.metadata.stats.num_fragments;
  if (modelEmpty) {
    var viewName = bubbleNode.name();
    var modelName = bubbleNode.getRootNode().children[0].name();
    console.warn("Empty View \"".concat(viewName, "\" in model \"").concat(modelName, "\"."));
  }
};

var isVectorFinite = function isVectorFinite(vec) {
  return isFinite(vec.x) && isFinite(vec.y) && isFinite(vec.z);
};

var isBoxFinite = function isBoxFinite(box) {
  return isVectorFinite(box.min) && isVectorFinite(box.max);
};

var getUpVector = function getUpVector(model) {
  var upVectorArray = model.getUpVector();
  return upVectorArray && new THREE.Vector3().fromArray(upVectorArray);
};

var isCameraValid = function isCameraValid(camera) {
  return (
    isVectorFinite(camera.position) &&
    isVectorFinite(camera.target) &&
    isVectorFinite(camera.up) &&
    isFinite(camera.orthoScale));

};

// Helper function for home camera:
// Check how many models are intersecting the frustum when using a certain camera.
var countCatchedModels = function countCatchedModels(models, camPos, frustum) {

  var catchedModels = 0;
  for (var i = 0; i < models.length; i++) {

    // model outside frustum? => consider as missed
    var model = models[i];
    var bbox = model.getBoundingBox();
    if (frustum.intersectsBox(bbox) === Autodesk.Viewing.Private.FrustumIntersector.OUTSIDE) {
      continue;
    }

    // also consider models as missed if they are extremely far away from the camera
    var diag = bbox.size().length;
    var dist = bbox.distanceToPoint(camPos);
    if (dist > diag * 50) {
      continue;
    }

    catchedModels++;
  }

  return catchedModels;
};


// As home camera for aggregated views, we use the default camera of one of the visible models.
// The choice is done in a way that we have as many visible models in frustum as possible.
var updateHomeCamera = function updateHomeCamera(viewer) {

  // Filter out any crappy models in advance (empty or infinite bbox)
  var models = viewer.getVisibleModels();
  models = models.filter(function (model) {return isBoxFinite(model.getBoundingBox());});

  var tmpCam = viewer.impl.camera.clone();
  var frustum = new Autodesk.Viewing.Private.FrustumIntersector();

  // Get model from the first model that defines it (must match anyway).
  // For the camera, we choose the default cam of the largest model (wrt. to data size).
  var upVector = undefined;
  var camera = undefined;
  var bestScore = undefined;
  for (var i = 0; i < models.length; i++) {
    var model = models[i];

    // Choose the first up-vector we get
    // We assume identical ones - otherwise, the aggregated model would be weird anyway.
    if (!upVector) {
      upVector = getUpVector(model);
    }

    // Consider default cam if valid
    var modelCam = model.getDefaultCamera();
    if (!modelCam || !isCameraValid(modelCam)) {
      continue;
    }

    var modelBox = model.getBoundingBox();

    // Configure frustum test for this camera
    tmpCam.position.copy(modelCam.position);
    tmpCam.target.copy(modelCam.target);
    tmpCam.up.copy(modelCam.up);
    tmpCam.isPerspective = modelCam.isPerspective;
    tmpCam.near = modelBox.distanceToPoint(tmpCam.position);
    tmpCam.far = tmpCam.near + modelBox.size().length;
    tmpCam.updateMatrixWorld();
    tmpCam.updateProjectionMatrix();
    tmpCam.matrixWorldInverse.getInverse(tmpCam.matrixWorld);
    frustum.reset(tmpCam);

    // Choose camera that catched most models
    var score = countCatchedModels(models, modelCam.position, frustum);
    if (!camera || score >= bestScore) {
      camera = modelCam;
      bestScore = score;
    }
  }

  if (!camera) {
    return;
  }

  // Extend camera by...
  //  1. pivot: Otherwise, autocam may leave the pivot point at some far-away position
  //     and the camera will orbit into void on next move.
  //  2. worldup: To make sure that we are not using the wrong axis for orbiting
  //  3. fov: always use reasonable default
  camera.pivot = camera.target;
  camera.worldup = upVector;
  camera.fov = 45.0;

  viewer.autocam.setHomeViewFrom(camera);
};

// Helper class to manage modelTheming state for a single model
var ModelThemingState = /*#__PURE__*/function () {

  function ModelThemingState(color) {_classCallCheck(this, ModelThemingState);
    // Currently assigned theming color
    this.color = color; // Vector4 

    // Used to avoid conflicts with DiffTool: If false, we don't touch theming colors until enabled again.
    this.enabled = true;

    // indiciates if the theming colors of the
    // model are currently applied
    this.active = false;
  }_createClass(ModelThemingState, [{ key: "setEnabled", value: function setEnabled(

    model, enable) {
      if (enable === this.enabled) {
        return;
      }
      this.enabled = enable;

      // revert theme colors if needed
      if (this.active && !enable) {
        model.clearThemingColors();
        this.active = false;
      } else {
        this.update(model);
      }
    } }, { key: "update", value: function update(

    model) {
      // Don't change theming if already applied or disabled
      if (this.active || !this.enabled) {
        return;
      }

      // set color for all dbIds that are associated with shapes. Note that some dbIds may appear more
      // than once, but this is not a problem here.
      var dbIds = model.myData.fragments.fragId2dbId;
      for (var i = 0; i < dbIds.length; i++) {
        var dbId = dbIds[i];
        model.setThemingColor(dbId, this.color);
      }

      this.active = true;
    } }]);return ModelThemingState;}();


export var AggregatedView = /*#__PURE__*/function () {

  function AggregatedView() {_classCallCheck(this, AggregatedView);

    // The purpose of the global offset is to avoid float inaccuracies for georeferenced
    // models with large offset. Note that we only use (0,0,0) as long as we don't
    // know anything better (see resetRefPoint())
    //
    // By default, LMV chooses the center of the model. This is okay for
    // a single model. But for multiple ones, it would mean to center all models
    // independently, so that their relative placement would be lost.
    this.globalOffset = undefined;

    // refPoint is chosen once per view switch and determined from the first model
    // to be shown. The globalOffset is only reset if too far away from the current refPoint.
    this.refPoint = undefined;

    // Contains modelItems for all models that are in memory or currently loading.
    // Indexed by modelKey (string).
    this.modelItems = {};

    // Indicates whether we have to setup LMV for a new view on next model add,
    // e.g., creating/updating the LMV toolbar and reset tools. We want to do this only
    // on startup or after an explicit view switch by the user.
    this.resetOnNextModelAdd = true;

    // In case resetOnNextModelAdd was triggered by showing a model, we save it here. This helps
    // avoids concurrency problems where more than one model is loaded at a time, i.e. a 2D sheet is loaded and
    // immediately a 3D model as well, but the 3D model triggers a reset, and only then the 2D sheet triggers it again
    // In that case, the UI will be reset according to the 2D, whereas the 3D model was loaded last. This variable
    // will ensure the last one that updated it will have the last word.
    this.resetTriggeringBubble = null;

    this.memTracker = null;

    this.cameraInitialized = false;

    // If true, the viewer is configured for 3D viewing, otherwise for 2d.
    // This flag is determined whenever adding the first model to an empty view.
    this.is3D = undefined;

    // If a camera is set before the global offset is determined, we keep it here and
    // apply it as soon as globalOffset is initialized.
    this.pendingCamera = null;

    // Since extension load is async, viewer.getExtension() may return null although we called loadExtension already.
    // For extensions that we load/unload dynamically, we track the state here - to avoid loading the same extension twice.
    this.extensionLoaded = {};

    this.loadersInProgress = {};

    // Current diff-view configuration.
    this.diff = {
      enabled: false,
      diffBubble: undefined,
      primaryBubble: undefined,
      diffBubbleLabel: undefined,
      primaryBubbleLabel: undefined,
      progressCallback: undefined,
      refNames: undefined };


    // Keys of all models that we keep ghosted during diff mode
    // (if they are not participating in the diff)
    this.modelIsGhosted = {};

    // Cache diffs to avoid frequent recomputation
    this.diffCache = [];

    // Set this to be notified when diff load/computation is finished
    this.onDiffDone = undefined;

    // Indicates that the first 3D model is loading and we have to wait for it 
    // to finish before we can load another model. This is needed to determine a consistent globalOffset.
    this.waitForFirstModel = false;

    // Mapping from modelKey to an object with {resolve, reject}, that will fulfill a pending load promise
    // when waitForFirstModel is done.
    this.loadPendingPromises = {};

    // Internally used array of callbacks that are triggered when a model is loaded.
    this.onLoad = [];

    // Internally used array of callbacks that are triggered when a model is unloaded.
    this.onUnload = [];

    // Optional: Assign theming color based on model (for 3D aggregated views)
    this.modelThemingStates = {}; // keys: modelId, values: ModelThemingState

    // Simplify debugging.
    // Note: This value is ONLY for debugging purposes and not supposed to be used in production code.
    window.LMV_MAIN_VIEW = this;

    // Only used if fetchAlignment is called before AlignmentService is ready
    this.pendingAlignmentFetches = []; // Promise[]
  }

  // @param {Object} [options]                      - Configuration options for aggregated view
  // @param {Object} [options.viewerConfig]         - Used for initializing GuiViewer3D
  // @param {bool}   [options.disableBookmarks]     - Disable display of visual bookmarks (
  // @param {Object} [options.clusterfck]           - Dependency-Injection of clusterfck library (enables clustering of Bookmark icons)
  // @param {function(av.BubbleNode)=>object} [options.getCustomLoadOptions]   - Allows for applying custom options to be used for all model loading.
  //                                                                             The callback returns an options object that is applied by default in all model-load calls.
  // @param {object} [options.viewerStartOptions] - Options passed to the viewer initialization process.
  // @param {Autodesk.Viewing.MultiViewerFactory} [options.multiViewerFactory] - Optional multi viewer factory. Used to create multiple viewers with shared resources.
  // @param {bool} [options.ignoreGlobalOffset]     - Forces globalOffset to undefined for all loaded models.
  //                                                  Effect of this is that all models are auto-centered using the model bbox.
  //                                                  Note that this does only work if you never show more than one 3D viewable at once.
  // @param {bool} [options.unloadUnfinishedModels] - By setting unloadUnfinishedModels, when calling hide(bubbleNode), it will unload models that haven't been fully loaded.
  //                                                  Used in order to reduce amount of file loaders when switching between models.
  // @param {bool} [options.useDynamicGlobalOffset] - If true, the globalOffset is applied dynamically after loading.
  // @param {function} options.createModelAlignmentService - Factory function to create AlignmentService implementation for loading/saving model transforms.
  //                                                         See AlignmentServices above for details.
  // @returns {Promise}                             - When resolved all required extensions are loaded.
  _createClass(AggregatedView, [{ key: "init", value: function init(parentDiv) {var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      this.initViewerInstance(parentDiv, options);

      this.memTracker = new av.ModelMemoryTracker(this.viewer, options.memoryLimit);

      // Activate transparency improvement by default
      this.viewer.impl.showTransparencyWhenMoving();

      // Activate dynamic globalOffset if wanted
      if (options.useDynamicGlobalOffset) {
        this.dynamicGlobalOffset = new DynamicGlobalOffset(this.viewer);
      }

      this.options = options;

      this._registerLmvEventListeners();
      return this._loadExtensions();
    }

    // Initialize a new viewer instance.
  }, { key: "initViewerInstance", value: function initViewerInstance(parentDiv) {var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      var ViewerClass = options.viewerClass || (options.headlessViewer ? av.Viewer3D : av.GuiViewer3D);

      // Disable auto-load of default hyperlink extension. Reasons:
      //  a) It produces asserts for background loading, because it assumes 'viewer.model' to exist
      //  b) Design Collaboration are using AEC hyperlink extension instead, and Docs are using
      //  another custom extension
      var config = options.viewerConfig || {};
      config.disabledExtensions = config.disabledExtensions || {};
      config.disabledExtensions.hyperlink = true;

      if (options.multiViewerFactory) {
        this.viewer = options.multiViewerFactory.createViewer(parentDiv, options.viewerConfig, ViewerClass);
      } else {
        this.viewer = new ViewerClass(parentDiv, options.viewerConfig);
      }

      this.viewer.start(undefined, undefined, undefined, undefined, options.viewerStartOptions);
    }

    /**
       * Method that can be overwritten to log errors to service. Default implementation just logs to console.error.
       * @param args The messages and other things that should be logged.
       * @private
       */ }, { key: "_onError", value: function _onError()
    {var _console;
      (_console = console).error.apply(_console, arguments);
    }

    // Resets tools, UI, camera, and refPoint. To be called when an explicit view switch occurred instead of just toggling visibility of models.
  }, { key: "reset", value: function reset() {

      // hide all previously visible models, so that there are no visible models in the viewer anymore.
      // This will make sure that the viewer will configured itself properly when adding the first new model.
      this.hideAll();

      // make sure that the camera is reset when new models are added
      this.pendingCamera = null;
      this.cameraInitialized = false;

      // Reset UI and tools on next model-add
      this.resetOnNextModelAdd = true;

      // Make sure that no LMV tools remain in the middle of an interaction - like measurements, sections etc.
      this._stopActiveTools();

      // Finish previous diff and discard cached diff results when switching to another view
      this._unloadDiffTool(true);

      // Consider that refPoint may change with next viewable
      this.refPoint = undefined;

      this.loadersInProgress = {};
    }

    // Find lmv model for given bubbleNode or key.
  }, { key: "getModel", value: function getModel(node) {
      var item = this._getItem(node);
      return item && item.model;
    }

    // Find lmv model for given bubbleNode or key.
    // If the model is not available yet, wait until it's ready.
  }, { key: "getModelAndWait", value: function getModelAndWait(node) {var _this = this;
      return new Promise(function (resolve) {
        var onEvent = function onEvent() {
          var model = _this.getModel(node);

          if (model) {
            _this.viewer.removeEventListener(Autodesk.Viewing.MODEL_ADDED_EVENT, onEvent);
            _this.onLoad.splice(_this.onLoad.indexOf(onEvent), 1);
            return resolve(model);
          }
        };

        var model = _this.getModel(node);

        if (model) {
          return resolve(model);
        }

        _this.viewer.addEventListener(Autodesk.Viewing.MODEL_ADDED_EVENT, onEvent);
        _this.onLoad.push(onEvent);
      });
    } }, { key: "isEmpty", value: function isEmpty()

    {
      return !this.getVisibleNodes().length;
    }

    // - If showing a 3D model, and until now all are 2D -> reset and set is3D = true
    //   (In this case areAllNodes2D doesn't include the newly added 3D model yet)
    // - If hiding a 3D model, and now all are 2D -> reset and set is3D = false
    //   (If we call this function after already hiding the 3D model, areAllNodes2D won't include it)
  }, { key: "_initForEnvironmentChange", value: function _initForEnvironmentChange(bubbleNode, isShowing) {
      if (this.areAllNodes2D() && bubbleNode.is3D()) {
        this.is3D = isShowing;

        var item;
        if (!isShowing) {
          // When hiding a 3D model, we need to update using one of the 2D models
          var filter2D = function filter2D(item) {return item.visible && item.node && item.node.is2D();};
          item = Object.values(this.modelItems).filter(filter2D)[0];
          bubbleNode = item.node;
        }

        // Make sure that the right extensions are loaded/unloaded
        this._updateExtensions(bubbleNode);
        // Before loading a 3D viewable, we must choose a refPoint for the view.
        this._updateRefPoint(bubbleNode);
        // Make sure the UI is reset for the correct mode
        if (isShowing) {
          this.resetOnNextModelAdd = true;
          this.resetTriggeringBubble = bubbleNode;
        } else if (item.model) {
          this._resetOnModeSwitch(item.model);
          this.viewer.impl.setUp2DMode(item.model, false);
          this.viewer.getExtension('Autodesk.ViewCubeUi', function (ext) {return ext.displayViewCube(false, false);});
        }
      }
    } }, { key: "_needsReset", value: function _needsReset(

    bubbleNode) {
      if (!this.resetOnNextModelAdd) {
        return false;
      }

      if (this.resetTriggeringBubble && this.resetTriggeringBubble !== bubbleNode) {
        return false;
      }

      return true;
    }

    // Makes sure that a model is loaded and shown
    // Returns a promise with the model
    // @param {BubbleNode} node
    // @param {Object} [customLoadOptions] - Optional extra loadOptions to override/extend the default ones. Used only if load is needed.
  }, { key: "show", value: function show(bubbleNode, customLoadOptions) {

      // Auto-Configure for 2D or 3D based on first node to show
      var isFirstViewable = this.isEmpty();
      if (isFirstViewable) {
        this._initForFirstViewable(bubbleNode);
      } else {
        this._initForEnvironmentChange(bubbleNode, true);
      }

      var modelKey = bubbleNode.getModelKey();

      // Either load model or show it directly
      var model = this.getModel(modelKey);
      var promise;
      if (model) {
        if (this._needsReset(bubbleNode)) {
          this.resetTriggeringBubble = null;
          this.resetOnNextModelAdd = false;
          this._resetOnModeSwitch(model);
        }
        // Need to set before the call to _showModel here, since _updateUpVector needs the updated value
        this.modelItems[modelKey].visible = true;
        this._showModel(model);
        promise = Promise.resolve(model);
      } else {
        // Model will be added later when root is loaded
        promise = this.load(bubbleNode, customLoadOptions);
        // After load(), the model item always exists
        this.modelItems[modelKey].visible = true;
      }

      // keep memTracker up-to-date about visible/used models
      this._updateModelTimestamps();

      // consolidate new models if possible
      this._consolidateVisibleModels();

      return promise;
    } }, { key: "hide", value: function hide(

    bubbleNode) {
      var item = this._getItem(bubbleNode);
      if (!item) {
        return;
      }

      if (this.options.unloadUnfinishedModels && (!item.model || !item.model.isLoadDone())) {
        this.unload(item.node); // Unload will cancel pending loaders in case there's no model yet
      } else if (item.model) {
        // Note that the model root might not be loaded yet. In this case, we don't need to call hideModel:
        // onModelLoaded() callback takes care that new models are only added if still set to visible.
        this.viewer.hideModel(item.model.id);
      }

      item.visible = false;

      updateHomeCamera(this.viewer); // Recompute home-view for remaining visible models
      this._updateUpVector();

      // Make sure that a model is unloaded immediately if memory is low
      this._cleanupModels();

      this._initForEnvironmentChange(item.node, false);
    } }, { key: "isVisible", value: function isVisible(

    bubbleNode) {
      var item = this._getItem(bubbleNode);
      return item && item.visible;
    } }, { key: "hideAll", value: function hideAll()

    {
      for (var key in this.modelItems) {
        this.hide(key);
      }
    } }, { key: "getVisibleNodes", value: function getVisibleNodes()

    {

      var nodes = [];
      for (var key in this.modelItems) {
        // Note that item.model may be null during loading, so we cannot use item.model.getDocumentNode()
        var item = this.modelItems[key];
        if (item.visible) {
          nodes.push(item.node);
        }
      }
      return nodes;
    } }, { key: "areAllNodes2D", value: function areAllNodes2D()

    {
      var nodes = this.getVisibleNodes();
      if (nodes.length) {
        // If the node is not a BubbleNode, it is an image, and it should be considered as 2d as well.
        return nodes.every(function (node) {return !(node instanceof Autodesk.Viewing.BubbleNode) || node.is2D();});
      }

      return false;
    }

    // Checks if the Otg manifest for a 3D viewable is available and complete. If not, it reports an error and returns false.
  }, { key: "isOtgManifestMissing", value: function isOtgManifestMissing(bubbleNode) {

      if (bubbleNode.is2D()) {
        return false;
      }

      var otgNode = bubbleNode.getOtgGraphicsNode();
      if (otgNode && !otgNode.error) {
        return false;
      }

      // This can only happen if the overall manifest conversion
      // succeeded, but conversion failed for some of its viewables.
      // A failure of the whole document conversion should have been handled outside on manifest load already.
      if (!otgNode) {
        this._onError("Otg node missing for viewable '".concat(bubbleNode.name(), "'."));
      } else {
        this._onError("Otg translation failed for viewable '".concat(bubbleNode.name(), "'. Error:"), otgNode.error);
      }

      if (this.onViewerNotification) {
        this.onViewerNotification('error', "Model Translation failed for viewable: '".concat(bubbleNode.name(), "'."));
      }

      return true;
    }

    // @param {BubbleNode} node 
    // @param {Object} [customLoadOptions] - Optional extra loadOptions to override/extend the default ones.
  }, { key: "load", value: function load(bubbleNode) {var _this2 = this;var customLoadOptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;

      // customLoadOptions is only provided if the client calls load() manually from outside. Usually, load() is rather called internally
      // by AggregatedView whenever we show a model that is not cached yet. For those internal load() calls, custom loadOptions are controlled
      // via a callback specified in AggregatedView options.
      if (!customLoadOptions && this.options.getCustomLoadOptions) {
        customLoadOptions = this.options.getCustomLoadOptions(bubbleNode);
      }

      // get or create model item
      var modelKey = makeKey(bubbleNode);
      var item = this.modelItems[modelKey];
      if (!item) {
        item = createModelItem(bubbleNode);
        this.modelItems[modelKey] = item;
      }

      // do nothing if model load was triggered before
      if (item.model) {
        return Promise.resolve(item.model);
      }
      if (item.loading || item.error) {
        return Promise.reject('Model load was already triggered');
      }

      // For 3D models, we need some extra code for globalOffset handling
      if (bubbleNode.is3D()) {

        // Defer loading if necessary
        if (this.waitForFirstModel) {
          // Another 3D viewable is loading. We have to wait for it, so that we 
          // know which globalOffset to use. Loading will be triggered in _modelRootLoadEnded() later.
          return new Promise(function (resolve, reject) {
            _this2.loadPendingPromises[modelKey] = { resolve: resolve, reject: reject };
          });
        }

        // If globalOffset is not initialized yet, block other models until this one is finished
        if (!this.options.ignoreGlobalOffset && !this.globalOffset) {
          this.waitForFirstModel = true;
        }
      }

      item.loading = true;

      // If client requires Otg for 3D viewables, check if Otg manifest is available and complete for 3D case.
      if (this.options.disableSvf && this.isOtgManifestMissing(bubbleNode)) {
        return Promise.reject('OTG manifest missing');
      }

      // Get LMV document to obtain acm session
      var root = bubbleNode.getRootNode();
      var doc = root.getDocument();

      var loadOptions = {
        // model is translated by -globalOffset. If undefined, LMV uses the model center.
        globalOffset: this.globalOffset,

        // consider geo-referencing for consistent placement: If the model contains a georeferencing transform,
        // it is applied by LMV at load-time.
        applyRefPoint: true,

        isAEC: true,

        // A too fine-grained BVH reduces the performance gain when using consolidation. This is avoided by using the recommended BVH defaults.
        // When setting useConsolidation to true (default for AEC), this would be done automatically. But this would also run consolidation preprocessing when
        // a model is loaded, which may temporarily affect the framerate and bypass the memory tracking. Therefore, we run consolidation
        // later as soon as all animations are finished.
        bvhOptions: avp.Consolidation.getDefaultBVHOptions(),
        useConsolidation: false,

        // We don't want LMV to auto-add models on load, but control adding of models ourselves instead. E.g., because:
        //  - The user might have toggled the model off or done a view switch meanwhile
        //  - The model might just be loaded for other purposes than show (e.g. support models for 2D diff)
        loadAsHidden: true,

        // Remember bubble node. This simplifies debugging, e.g. by helping to get the view name for a model.
        bubbleNode: bubbleNode,

        // Reduce memory consumption by computing bounding boxes on-the-fly. This is only a problem for the model explode tool, which we do not use.
        disablePrecomputedNodeBoxes: true,

        // Avoid LMV from auto-resetting the camera on model-add. E.g., we don't want to jump the camera around just because we toggled visibility of a model.
        preserveView: true,

        // Track progress of fragment list loading (used for diff progress bar)
        onFragmentListLoadProgress: function onFragmentListLoadProgress() {return _this2._updateDiffLoadProgress();},

        // Finding layers in propDb adds significant effort to the worker. If any client of AggregatedView wants to use 3DModelLayers,
        // we can add an option for that - or allow some general customization of load options.
        disable3DModelLayers: true,

        // Avoid loadDocumentNode() from auto-unloading all other models
        keepCurrentModels: true,

        // Prevent the creation of LMV's default UI.
        headlessViewer: this.options.headlessViewer,

        // Load Leaflet models in page coordinates by default.
        leafletOptions: { fitPaperSize: true } };


      var onModelLoaded = this._onModelLoaded.bind(this);
      var onModelLoadFailed = function onModelLoadFailed(errorCode) {return _this2._onModelLoadFailed(bubbleNode, errorCode);};

      if (customLoadOptions) {
        Object.assign(loadOptions, customLoadOptions);
      }

      return new Promise( /*#__PURE__*/function () {var _ref = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee(resolve, reject) {return regeneratorRuntime.wrap(function _callee$(_context) {while (1) {switch (_context.prev = _context.next) {case 0:

                  // Allow alignmentService to customize the refPointTransform in the loadOptions. If no service or no matrix is specified,
                  // applyAlignmentservice() just resolves immediately (default).
                  _this2._applyAlignmentService(bubbleNode, loadOptions).then(
                  function () {return _this2.viewer.loadDocumentNode(doc, bubbleNode, loadOptions);}).
                  then(function (model) {
                    onModelLoaded(model);
                    resolve(model);
                  }).catch(function (error) {
                    onModelLoadFailed(error);
                    reject(error);
                  });case 1:case "end":return _context.stop();}}}, _callee);}));return function (_x, _x2) {return _ref.apply(this, arguments);};}());

    } }, { key: "unloadUnderlayRaster", value: function unloadUnderlayRaster(

    bubbleNode) {
      var models = this.viewer.getAllModels();
      var underlayRasterModel = models.find(function (model) {var _docNode$getDocument, _bubbleNode$getDocume;
        var docNode = model.getDocumentNode();
        return docNode && ((_docNode$getDocument = docNode.getDocument()) === null || _docNode$getDocument === void 0 ? void 0 : _docNode$getDocument.getPath()) === ((_bubbleNode$getDocume = bubbleNode.getDocument()) === null || _bubbleNode$getDocume === void 0 ? void 0 : _bubbleNode$getDocume.getPath()) &&
        docNode.guid() === bubbleNode.guid() && model.getData().underlayRaster;
      });

      if (underlayRasterModel) {
        this.viewer.unloadModel(underlayRasterModel);
      }
    } }, { key: "unload", value: function unload(

    bubbleNode) {
      this.unloadUnderlayRaster(bubbleNode);

      this.viewer.unloadDocumentNode(bubbleNode);

      // Usually, item will exist - unless the model was not loaded via AggregatedView.
      var item = this._getItem(bubbleNode);
      if (item) {
        // Signal that model loading was cancelled.
        // Why needed?: The model we just unloaded might have been the first one - and others might be waiting for it to obtain the globalOffset.
        if (item.loading) {
          this._onModelLoadEnded(item);
        }

        // Remove model from overall geometry-load progress tracking.
        // TODO: We shouldn't have to care for this here. We should better add general support of aggregated load progress in Viewer3D directly.
        if (item.model) {
          delete this.loadersInProgress[item.model.id];
        }
      }

      // delete item state
      var key = makeKey(bubbleNode);
      delete this.modelItems[key];

      // set themingState to inactive, so that colors are re-applied in case the model is reloaded
      var state = this.modelThemingStates[key];
      if (state) {
        state.active = false;
      }

      // Notify listener callbacks
      this.onUnload.forEach(function (cb) {return cb(bubbleNode);});
    } }, { key: "unloadAll", value: function unloadAll(

    itemFilter) {
      var keys = Object.keys(this.modelItems).slice();
      for (var i = 0; i < keys.length; i++) {
        var key = keys[i];
        var item = this.modelItems[key];

        if (!itemFilter || itemFilter(item)) {
          this.unload(item.node);
        }
      }
    }

    // Set camera in global coords.
    //  - The current global offset is automatically subtracted
    //  - You don't have to specify all members, e.g., can leave out up or fov. Only defined values are replaced.
    //  - You can call it independent of loading state: If no model is loaded yet, the camera change is applied after first model add
    //  - Note that the call only has effect on current view, i.e., is discarded on reset/viewSwitch calls.
  }, { key: "setCameraGlobal", value: function setCameraGlobal(camera) {
      // We copy the vector-values to avoid time-dependent traps if input vectors are changed after the call
      this.pendingCamera = {
        position: camera.position && new THREE.Vector3().copy(camera.position),
        target: camera.target && new THREE.Vector3().copy(camera.target),
        up: camera.up && new THREE.Vector3().copy(camera.up),
        fov: camera.fov,
        isPerspective: camera.isPerspective };


      // Apply camera right now if possible - otherwise later after camera initialize on first model add
      this._applyPendingCameraWhenReady();
    } }, { key: "setCamera", value: function setCamera(

    camera) {
      this.viewer.impl.setViewFromCamera(camera, true);
      this.cameraInitialized = true;
    } }, { key: "startBimWalk", value: function startBimWalk()

    {
      this.bimWalkStartPending = true;
      this._startBimWalkWhenReady();
    } }, { key: "stopBimWalk", value: function stopBimWalk()

    {
      var ext = this.viewer.getExtension(ExtNames.BimWalk);
      if (ext && ext.isActive()) {
        ext.deactivate();
      }
      // If a start was pending, cancel it.
      this.bimWalkStartPending = false;
    } }, { key: "isBimWalkActive", value: function isBimWalkActive()

    {
      var ext = this.viewer.getExtension('Autodesk.BimWalk');
      return ext && ext.activeStatus || this.bimWalkStartPending;
    }

    // Use this for explicit view switches. See setNodes() for params.
  }, { key: "switchView", value: function switchView(bubbleNodes, diffConfig) {
      this.reset();
      this.setNodes(bubbleNodes, diffConfig);
    }

    // Load/Unload models so that the currently loaded models matches with the given list of svfs.
    //
    // Note: Use switchView() to do an explicit view switch (including resetting tools/camera)
    //       Use setModels() to reconfiguring which models are visible in the current view.
    //
    //  @param {av.BubbleNode|av.BubbleNode[]} bubbleNodes  - The nodes to be shown.
    //  @param {Object]          [diffConfig]               - Options to activate diff views.
    //
    // A diffConfig contains:
    //  @param {av.BubbleNode[]} [diff.primaryBubbles]      - A subset of 'bubbleNodes' that participates in the diff. If 'bubbleNodes' contains more, these will be ghosted.
    //                                                        These nodes represent the current/as-is state.
    //  @param {av.BubbleNode[]} [diff.diffBubbles]         - Length must match primaryBubbles. For each node primaryBubbles[i], diffBubbles[i] provides the corresponding "before" state to compare against.
    //  @param {av.BubbleNode}   [diff.supportBubbles.diff] - If svfs are sheet nodes, diff.supportModels must provide
    //                                                        the bubbleNodes for the corresponding 3D support models. { diff, primary }
    //  @param {av.BubbleNode}   [diff.supportBubbles.primary]
    //  @param {bool}            [diff.supportBubbles.autoDetect] - If true, support models are automatically found - works for Revit models with master views.
  }, { key: "setNodes", value: function setNodes(bubbleNodes, diffConfig) {var _this3 = this;

      // Don't be pedantic if just called with a single node or null
      bubbleNodes = bubbleNodes || [];
      bubbleNodes = bubbleNodes instanceof av.BubbleNode ? [bubbleNodes] : bubbleNodes;

      // Do batch-request for all alignment transforms that we may need
      this.fetchAlignmentsForNodes(bubbleNodes);

      // Collect nodes that are to be changed to visible
      var modelMustBeShown = function modelMustBeShown(node) {return !_this3.isVisible(node);};
      var modelsToShow = bubbleNodes.filter(modelMustBeShown); // {LMVModelLink[]}

      // Create temp object to check which nodes finally to be shown
      var newModelKeys = {}; // {string[]}
      bubbleNodes.forEach(function (node) {newModelKeys[node.getModelKey()] = true;});

      // Collect nodes to be unloaded
      var modelMustBeHidden = function modelMustBeHidden(node) {return newModelKeys[node.getModelKey()] === undefined;};
      var modelsToHide = this.getVisibleNodes().filter(modelMustBeHidden); // {av.BubbleNode[]}

      // Unload first. This will also reset the global offset if the set of model changed completely
      modelsToHide.forEach(function (svf) {_this3.hide(svf);});

      // Load all new models
      modelsToShow.forEach(function (node) {_this3.show(node);});

      // Enable and configure diff, or disable if diffConfig is null.
      this._setDiff(diffConfig);
    }

    // May be null if extension is not loaded yet.
  }, { key: "getFloorSelector", value: function getFloorSelector() {
      return this.levelsExtension && this.levelsExtension.floorSelector;
    }

    // Define the set of BubbleNodes for which we create InCanvas-Bookmarks.
    //  @param {BubbleNode[]} bookmarks
  }, { key: "setBookmarks", value: function setBookmarks(bookmarks) {
      this.bookmarks = bookmarks;
      this._updateBookmarks();
    }

    // Returns true if all pending loading is finished. More concrete, it means that there is no...
    //  - model-root loading
    //  - geometry loading, or
    //  - propDbLoading
    // pending or in progress.
  }, { key: "isLoadDone", value: function isLoadDone() {
      for (var key in this.modelItems) {
        var item = this.modelItems[key];
        var model = item && item.model;
        var modelRootPending = !model && !item.error;
        var geomPending = model && !model.isLoadDone();
        var propDbPending = model && model.getPropertyDb() && !model.getPropertyDb().isLoadDone();
        var texLoading = avp.TextureLoader.requestsInProgress() > 0;

        if (modelRootPending || geomPending || propDbPending || texLoading) {
          return false;
        }
      }
      return true;
    }

    // Returns a promise that resolves when isLoadDone() returns true.
  }, { key: "waitForLoadDone", value: function waitForLoadDone() {var _this4 = this;
      return new Promise(function (resolve) {

        if (_this4.isLoadDone()) {
          resolve();
        }

        // On each load-relevant event, check if loading is finished.
        var onEvent = function onEvent() {

          if (!_this4.isLoadDone()) {
            return;
          }

          _this4.viewer.removeEventListener(av.GEOMETRY_LOADED_EVENT, onEvent);
          _this4.viewer.removeEventListener(av.OBJECT_TREE_CREATED_EVENT, onEvent);
          _this4.viewer.removeEventListener(av.TEXTURES_LOADED_EVENT, onEvent);
          _this4.onUnload.splice(_this4.onUnload.indexOf(onEvent), 1);

          resolve();
        };

        // register event listeners to try again if something changes
        _this4.viewer.addEventListener(av.GEOMETRY_LOADED_EVENT, onEvent);
        _this4.viewer.addEventListener(av.OBJECT_TREE_CREATED_EVENT, onEvent);
        _this4.viewer.addEventListener(av.TEXTURES_LOADED_EVENT, onEvent);
        _this4.onUnload.push(onEvent);
      });
    }

    // Register a service to load custom alignment transforms. Must be specified before model loading starts.
    //
    //  @param {AlignmentService} alignmentService - Must implement alignmentService.loadTransform() function.
  }, { key: "setAlignmentService", value: function setAlignmentService(alignmentService) {
      this.alignmentService = alignmentService;
    }

    // Make batch request to get alignment transforms for all models that we are going to display.
    // This avoids that we do not make individual requests for each model to be loaded.
    //
    // Note:
    //   If you use setNodes() or switchView() with all nodes to be shown at once, this function is called automatically
    //   You only need to call fetchAlignmets() yourself if you call show/load directly or if the first call to setNodes() doesn't contain
    //   all models yet (e.g. because only a subset of manifests is loaded).
    // 
    //  @param {string[]} versionUrns
  }, { key: "fetchAlignments", value: function () {var _fetchAlignments = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee3(urns) {var _this5 = this;var doFetch, promise;return regeneratorRuntime.wrap(function _callee3$(_context3) {while (1) {switch (_context3.prev = _context3.next) {case 0:if (


                this.isUsingAlignmentService()) {_context3.next = 2;break;}return _context3.abrupt("return");case 2:



                doFetch = /*#__PURE__*/function () {var _ref2 = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee2() {var alignmentService;return regeneratorRuntime.wrap(function _callee2$(_context2) {while (1) {switch (_context2.prev = _context2.next) {case 0:_context2.next = 2;return (


                              _this5.getAlignmentService());case 2:alignmentService = _context2.sent;_context2.next = 5;return (




                              alignmentService.fetchItems(urns));case 5:case "end":return _context2.stop();}}}, _callee2);}));return function doFetch() {return _ref2.apply(this, arguments);};}();


                // Keep promise, so that we can wait for it before we load anything.
                promise = doFetch();
                this.pendingAlignmentFetches.push(promise);

                // Resolve afer fetch is done
                _context3.next = 7;return promise;case 7:return _context3.abrupt("return", _context3.sent);case 8:case "end":return _context3.stop();}}}, _callee3, this);}));function fetchAlignments(_x3) {return _fetchAlignments.apply(this, arguments);}return fetchAlignments;}()


    // Shortcut to fetch alignments for BubbleNodes
  }, { key: "fetchAlignmentsForNodes", value: function fetchAlignmentsForNodes(nodes) {
      var urns = nodes === null || nodes === void 0 ? void 0 : nodes.map(function (node) {return node.getRootNode().urn();});
      this.fetchAlignments(urns);
    }

    // "Support models" are 3D models that augment 2D diffs for better results: Instead of comparing the 2D objects directly,
    // the correspodning 3D counterparts are compared. This is more reliable and avoids false positives due to irrelevant plotting differences.
    // We use master views as support models, because they contain all objects of a given phase.
    //
    // Given a bubble node referring to a 2D sheet node, this function returns
    // the bubbleNode of the corresponding 3D master view that belongs to the same phase.
    // This can be used as "support model" to obtain better results for 2D diff.
  }, { key: "_setDiff",
























    // ---------------------------
    // Internal member functions:
    // ---------------------------
    value: function _setDiff(
    diff) {
      if (diff) {
        this.diff.diffBubbles = diff.diffBubbles;
        this.diff.primaryBubbles = diff.primaryBubbles;
        this.diff.diffBubbleLabel = diff.diffBubbleLabel;
        this.diff.primaryBubbleLabel = diff.primaryBubbleLabel;
        this.diff.progressCallback = diff.progressCallback;
        this.diff.supportBubbles = diff.supportBubbles;
        this.diff.empty = diff.empty;
        this.diff.refNames = diff.refNames;

        // If support models are needed, make sure that they are loaded (if necessary)
        var support = diff.supportBubbles;
        if (support) {
          // Detect support bubbles if wanted
          support.autoDetect && AggregatedView.findDiffSupportModels(diff);

          // Only use support models if we succeeded to find them for diff and primary
          if (support.diff && support.primary) {
            // Load support models
            this.load(support.primary);
            this.load(support.diff);
          } else {
            // We couldn't find support models

            // Check if it's a 2D Diff (otherwise we don't need supportBubbles)
            var node = diff.diffBubbles && diff.diffBubbles[0];
            var is2D = node && node.is2D();

            // If we have to run 2D diff without support models, we better warn,
            // because the diff results may not be ideal.
            is2D && console.warn('Could not find support models for 2D diff.');
            this.diff.supportBubbles = null;
          }
        }

        // start progress bar right now - so that it is also visible if
        // we have to load some models first.
        if (!diff.empty && diff.progressCallback) {
          this._signalDiffLoadProgress(0, true);
        }

        this.diffStartTime = performance.now();
      } else
      {
        this.diff.diffBubbles = undefined;
        this.diff.primaryBubbles = undefined;
        this.diff.diffBubbleLabel = undefined;
        this.diff.primaryBubbleLabel = undefined;
        this.diff.progressCallback = undefined;
        this.diff.supportBubbles = undefined;
        this.diff.refNames = undefined;
      }
      this.diff.enabled = Boolean(diff);
      this.diffNeedsUpdate = true;
      this._updateDiff();
    }

    // Set the correct world up vector when switching from 3D to 2D
  }, { key: "_updateUpVector", value: function _updateUpVector() {
      var worldUpVector;
      if (this.areAllNodes2D()) {
        worldUpVector = new THREE.Vector3(0, 1, 0);
      } else {
        // Get vector from the first 3D model
        var filter3D = function filter3D(item) {return item.visible && item.node && item.node.is3D();};
        var item = Object.values(this.modelItems).filter(filter3D)[0];
        if (item && item.model) {
          worldUpVector = getUpVector(item.model);
        } else {
          worldUpVector = new THREE.Vector3(0, 0, 1); // A default up vector for 3D
        }
      }
      this.viewer.navigation.setWorldUpVector(worldUpVector, false, true);
    }

    // add model to LMV scene (must be in memory)
  }, { key: "_showModel", value: function _showModel(model) {

      // In rare cases, the model geometry might be empty. For this case, we don't add the model to LMV.
      // This avoids the pedantic "Model is empty" warning and possibly other detail issues due to an empty model bbox.
      // Note that we don't want this warning, because it is not a big problem if one out of many models is empty.
      // E.g., this may happen if a model has no geometry for the selected phase.
      if (model.is3d() && !model.hasGeometry()) {
        var modelName = model.getDocumentNode().getRootNode().children[0].name();
        console.warn('Ignored model with empty geometry: ', modelName);
        return;
      }

      // Skip any automatic tool reset from LMV. We trigger these explicitly,
      // but only if an explicit view switch (=> LMVViewer.reset()) has occurred.
      // Reason: We only want tool resets on explicit view switches. The scene may
      //         also be just temporarily empty when toggling visibility of models.
      //         In this case, we don't want any automagic resets.
      var preserveTools = true;
      this.viewer.showModel(model.id, preserveTools);

      // Consider new visible model for home camera
      updateHomeCamera(this.viewer);
      this._updateUpVector();
    }

    // Reset UI when switching from 2D or 3D (or when loading for the first time)
  }, { key: "_resetOnModeSwitch", value: function _resetOnModeSwitch(model) {var _this6 = this;
      // Unload ZoomWindow and reload after recreating UI
      if (this.viewer.getExtension(ExtNames.ZoomWindow)) {
        this.viewer.unloadExtension(ExtNames.ZoomWindow);
      }

      if (!this.options.headlessViewer) {
        this.viewer.createUI(model, true);
      }

      // Temporarily remove lock to make sure the navigation tool is changed
      var prevLock = this.viewer.setNavigationLock(false);
      this.viewer.activateDefaultNavigationTools(!this.is3D);
      this.viewer.setNavigationLock(prevLock);

      this.viewer.navigation.setIs2D(!this.is3D);

      // Integrate the zoom window functionality. This also automatically
      // moves the Zoom tool into this submenu.
      this._loadExtension(ExtNames.ZoomWindow).catch(function (err) {return _this6._onError(err);});

      // If startBimWalk() was called for this view, activate it
      this._startBimWalkWhenReady();
    } }, { key: "_onModelAdded", value: function _onModelAdded(

    event) {

      var model = event.model;

      // if camera is not initialized yet, use default camera from the first added model.
      // TODO: Think about some cleaner choice of the start camera that doesn't depend on
      //       what is loaded first. But it's not fully clear how to define it, because
      //       the list of displayed models may change arbitrarily and we don't know bboxes in advance...
      if (!this.cameraInitialized) {
        this._initCamera(model);
      }

      if (model.getData().underlayRaster) {
        return;
      }

      // make sure that the model is rendered using the current viewer's globalOffset
      if (model.is3d() && this.options.useDynamicGlobalOffset) {
        model.setGlobalOffset(this.viewer.impl.camera.globalOffset);
        this.viewer.impl.onModelTransformChanged(model);
      }

      var node = event.model.getDocumentNode();

      if (this._needsReset(node)) {
        this.resetTriggeringBubble = null;
        this.resetOnNextModelAdd = false;
        this._resetOnModeSwitch(model);
      }

      // log console warning if model is empty
      warnIfModelEmpty(model);

      // Image files don't have a document node. In that case, skip ghosting part, which is not relevant to images anyway.
      if (node) {
        // Make sure that ghosting is applied if needed. Removing a model resets visibility,
        // so we assume isModelGhosted as false.

        var modelKey = node.getModelKey();
        delete this.modelIsGhosted[modelKey];
        this._updateGhosting();
      }
    }

    // Called whenever the geometry of a model has finished loading.
  }, { key: "_onGeometryLoaded", value: function _onGeometryLoaded(event) {
      this._consolidateVisibleModels();
      this._updateDiff();
      this.loadersInProgress[event.model.id] = 100;
    } }, { key: "_onExtensionLoaded", value: function _onExtensionLoaded(

    event) {
      var extName = event.extensionId;

      // If startBimWalk has been called before BimWalk was loaded, start it when ready
      this._startBimWalkWhenReady();

      if (extName === ExtNames.Levels) {
        this.levelsExtension = this.viewer.getExtension(ExtNames.Levels);
      }

      // Make sure that BookmarksExtension gets current bookmarks when loaded
      if (extName === ExtNames.Bookmarks) {
        this._updateBookmarks();
      }
    } }, { key: "_registerLmvEventListeners", value: function _registerLmvEventListeners()

    {
      // As soon as all geometry is loaded, make sure that consolidation is triggered if the transition is already finished.
      // Note that this also includes to cleanup memory if necessary.
      this.viewer.addEventListener(av.GEOMETRY_LOADED_EVENT, this._onGeometryLoaded.bind(this));

      // Update UI if a new model is added
      this.viewer.addEventListener(av.MODEL_ADDED_EVENT, this._onModelAdded.bind(this));

      this.viewer.addEventListener(av.EXTENSION_LOADED_EVENT, this._onExtensionLoaded.bind(this));

      // Compute overall loading progress
      this.viewer.addEventListener(av.PROGRESS_UPDATE_EVENT, this._onProgressUpdate.bind(this));

      // If diff is enabled, make sure that it starts as soon as all required models are loaded
      // For this, we don't need all geometry, but other data (fragments loaded + propDbLoader created).
      // NOTE: We cannot do this in the onModelLoaded() callback, because only the MODEL_ROOT_LOADED_EVENT
      //       is called AFTER loading the whole fragment list and creating the propDbLoader.
      this.viewer.addEventListener(av.MODEL_ROOT_LOADED_EVENT, this._updateDiff.bind(this));
    }

    // Makes sure that all visible models and fully loaded models are consolidated if memory allows it.
    // Triggered whenever a model finished loading, added, or if new memory gets available.
  }, { key: "_consolidateVisibleModels", value: function _consolidateVisibleModels() {

      // Free some memory if needed and possible
      this._cleanupModels();

      //Skip consolidation on mobile due to more limited memory on weaker devices.
      if (isMobileDevice())
      return;

      //This duplicates logic from Viewer3D.loadDocumentNode, because AggregateView does its
      //loadModel calls manually. We need this flag to be able to debug memory issues in Design Collab
      var cparam = getParameterByName("useConsolidation");
      if (cparam === "false")
      return;

      // For each 3D model, trigger consolidation if needed and possible
      var visibleModels = this.viewer.getVisibleModels();
      for (var i = 0; i < visibleModels.length; i++) {
        var model = visibleModels[i];

        // Don't consolidate anything if we are running out of memory
        if (this.memTracker.memoryExceeded()) {
          return;
        }

        // Consolidation requires model + all geometry to be loaded
        var modelLoaded = model && model.isLoadDone();
        if (!modelLoaded) {
          return;
        }

        // Skip anything 2D (sheets/leaflets)
        if (model.is2d()) {
          return;
        }

        // Consolidate it if not done already
        if (!model.isConsolidated()) {
          // consolidate model
          var glRenderer = this.viewer.impl.glrenderer();
          var materials = this.viewer.impl.getMaterials();
          model.consolidate(materials, undefined, glRenderer, true);

          // Consolidation raises mem-consumption. => Run cleanup again.
          this._cleanupModels();
        }
      }
    }

    // Makes sure that long unused models are removed deleted to free memory if needed.
    //
    // It is called whenever either...
    //  a) A model goes out of use (hideModel() call)
    //  b) Memory consumption has grown (model geometry loaded or model consolidation was run)
  }, { key: "_cleanupModels", value: function _cleanupModels() {var _this7 = this;

      // Make sure that LRU timestamps are up-to-date.
      this._updateModelTimestamps();

      // define customized unloadModel function
      var unloadModel = function unloadModel(model) {
        var node = model.getDocumentNode();

        // Usually, the model supposed to be uploaded will not be in use.
        // But: In rare cases, the geometryLoaded event of a model might arrive earlier than the onDone() callback.
        // Reason is that the onDone() callback is delayed by a setTimeout in GuiViewer.onSuccessChanged().
        // Such a model is added to the viewer before we even know about it. Therefore, it doesn't have any timestamp yet.
        //
        // If this happens in combination with a "close to memory limit" scenario, the model is incorrectly classified as "unused"
        // by ModelMemoryTracker. TODO: Check if we can find a more elegant solution to prevent this problem.
        var item = _this7._getItem(node);
        if (item && item.visible) {
          return;
        }

        _this7.unload(node);
      };

      // let memTracker free memory if necessary
      this.memTracker.cleanup(unloadModel);
    }

    // Update LRU timestamps for all models that are currently in use.
    // We must overload/customize this function to consider the 2d support models
  }, { key: "_updateModelTimestamps", value: function _updateModelTimestamps() {
      var visibleModels = this.viewer.getVisibleModels().slice();

      // Consider diff support models if 2D diff is active
      var supp = this._getDiffSupportModels();
      if (this.diff.enabled && supp) {
        if (supp.diff) visibleModels.push(supp.diff);
        if (supp.primary) visibleModels.push(supp.primary);
      }

      this.memTracker.updateModelTimestamps(visibleModels);
    } }, { key: "_stopActiveTools", value: function _stopActiveTools()

    {
      var sectionExtension = this.viewer.getExtension('Autodesk.Section');
      if (sectionExtension && sectionExtension.isActive()) {
        sectionExtension.enableSectionTool(false);
      }

      this.stopBimWalk();

      // Pass false to the getPropertyPanel() method, otherwise it would try to create the
      // property panel if not yet existing.
      var propertyPanel = !this.options.headlessViewer && this.viewer.getPropertyPanel(false);
      if (propertyPanel && propertyPanel.isVisible()) {
        propertyPanel.setVisible(false);
      }
    } }, { key: "_onModelLoaded", value: function _onModelLoaded(

    model) {

      // get model item
      var item = this._getItem(model);
      if (!item) {
        // This can only happen if unload() has been called after load meanwhile.
        // In this case, we don't need the model anymore.
        return;
      }

      // GlobalOffset handling
      if (model.is3d()) {

        // When using dynamic globalOffset, we don't have to care about consistent globalOffsets before model loading, but just apply the consistent offset after loading.
        if (this.options.useDynamicGlobalOffset) {
          // Dynamic globalOffset handling

          // Apply current globalOffset value of the viewer camera.
          model.setGlobalOffset(this.viewer.impl.camera.globalOffset);
        } else {
          // Static globalOffset handling

          // If this is the first viewable, we use it to initialize the global offset.
          // We don't need this if we use dynamic global offsets.
          if (this.waitForFirstModel && !this.options.ignoreGlobalOffset) {
            this.globalOffset = new THREE.Vector3().copy(model.myData.globalOffset);
            this._onGlobalOffsetChanged();
          }
        }
      }

      // Store model in modelItem
      item.model = model;

      // If item is still set to visible, add it
      if (item.visible) {
        this._showModel(item.model);
      }

      // Make sure that the new model gets latest timestamp for LRU caching
      this._updateModelTimestamps();

      // Update diff progress bar if a model-root of a diffModel was loaded
      this._updateDiffLoadProgress();

      // Make sure that latest theming state is applied
      this._updateModelTheming(model);

      this._onModelLoadEnded(item);
    } }, { key: "_onModelLoadFailed", value: function _onModelLoadFailed(

    bubbleNode, errorCode) {
      this._onError("Failed to load model: ".concat(bubbleNode.name(), ". Error code: ").concat(errorCode));
      var item = this._getItem(bubbleNode);
      if (item) {
        item.error = true;
        this._onModelLoadEnded(item);
      }
    }

    // Called if model-root load succeeded, failed, or was cancelled
  }, { key: "_onModelLoadEnded", value: function _onModelLoadEnded(item) {var _this8 = this;
      item.loading = false;
      var bubbleNode = item.node;

      // Allow other models to load
      if (bubbleNode.is3D() && this.waitForFirstModel) {
        // Unblock 3D model loading
        this.waitForFirstModel = false;

        // Trigger loading of any models for which loading was deferred.
        var _loop = function _loop(key) {
          // Note that load() already takes care that nothing is loaded twice
          var node = _this8.modelItems[key].node;var _ref3 =
          _this8.loadPendingPromises[key] || {},resolve = _ref3.resolve,reject = _ref3.reject;
          _this8.load(node).then(function (model) {
            if (resolve)
            resolve(model);
          }).catch(function (e) {
            if (reject)
            reject(e);
          });};for (var key in this.modelItems) {_loop(key);
        }

        this.loadPendingPromises = {};
      }

      // Notify listener callbacks
      this.onLoad.forEach(function (cb) {return cb(bubbleNode);});
    }

    /* @param {av.Model} */ }, { key: "_initCamera", value: function _initCamera(
    model, withTransition) {
      this.viewer.impl.setViewFromFile(model, !withTransition);

      // Makes sure that the viewer home button is also properly working for 2D sheets.
      this.viewer.impl.controls.recordHomeView();

      this.cameraInitialized = true;

      // If there was a setCameraGlobal that we couldn't apply immediately, do it now
      if (this.pendingCamera) {
        this._applyPendingCameraWhenReady();
      }
    }

    // Returns item for a given bubbleNode/key/model - or undefined if unknown.
  }, { key: "_getItem", value: function _getItem(bubbleNode) {
      var key = makeKey(bubbleNode);
      return this.modelItems[key];
    }

    // The refPoint is a 3d position from which we know that all 3d viewables to display must be close to it.
    // We use the refPoint to determine the globalOffset that we use in LMV for loadOptions.
    // If the refPoint changes slightly, globalOffset may remain the same. But if it is by a large value,
    // the global offset needs to be updated - which requires a reload for all models.
    //
    // Purpose of all this is to determine in advance which globalOffset we have to use for loading. The globalOffset must be...
    //    1. close to the geo-coords of each displayed model
    //    2. known in advance (because it is used for loadOptions)
    //    3. identical for all models displayed at once
    //
    // @param {BubbleNode} bubbleNode - node that is about to be shown
  }, { key: "_updateRefPoint", value: function _updateRefPoint(bubbleNode) {

      // We don't need to do anything here if we use dynamic global offsets.
      if (this.options.useDynamicGlobalOffset) {
        return;
      }

      var isFirst3DViewable = this.isEmpty() || this.areAllNodes2D();
      if (!isFirst3DViewable || !bubbleNode.is3D() || this.options.ignoreGlobalOffset) {
        return;
      }

      // Following code assumes that getAecModelData is called after AecModelMata was downloaded.
      // Having no AECModelData in this case means that this is a model other than Revit
      // and setting globalOffset to zero will essentially reset the potentially existing correct global offset
      // If we encounter such a scenario (e.g. IFC model), we just skip updating global offset here
      var aecModelData = bubbleNode.getAecModelData();
      if (!aecModelData) {
        return;
      }

      var tf = aecModelData && aecModelData.refPointTransformation; // Matrix4x3 as array[12]
      this.refPoint = tf ?
      { x: tf[9], y: tf[10], z: 0.0 } : // refPoint = refPointTransform * (0,0,0)
      { x: 0, y: 0, z: 0 }; // fallback: use origin if we have no aecModelData

      // Workaround: We apply the global offset only in (x,y), because:
      //  a) The large offsets are usually only happening for (x,y)
      //  b) An offset in z would need special handling in the LevelsExtension

      // Check if the current globalOffset is sufficiently close to the refPoint to avoid inaccuracies.
      var MaxDistSqr = 4.0e6;
      var distSqr = this.globalOffset && THREE.Vector3.prototype.distanceToSquared.call(this.refPoint, this.globalOffset);
      if (!this.globalOffset || distSqr > MaxDistSqr) {

        this.globalOffset = new THREE.Vector3().copy(this.refPoint);

        // unload all previous 3D models that we loaded with previous geo offset
        this.unloadAll(function (item) {return item.model && item.model.is3d();});

        this._onGlobalOffsetChanged();
      }
    } }, { key: "_onGlobalOffsetChanged", value: function _onGlobalOffsetChanged()

    {
      // Make sure that bookmark positions are calculated based on the latest global offset
      var bookmarkExt = this.viewer.getExtension(ExtNames.Bookmarks);
      if (bookmarkExt) {
        bookmarkExt.resetGlobalOffset(this.globalOffset);
      }
    }

    // Default extensions as used by Design Collaboration
  }, { key: "_getDefaultExtensions", value: function _getDefaultExtensions() {var _this9 = this;
      return [
      {
        name: ExtNames.CrossFade,
        getLoadingCondition: function getLoadingCondition() {return !av.isMobileDevice();} },

      {
        name: ExtNames.Levels,
        getOptions: function getOptions() {return _this9.viewer.config;} },

      {
        name: ExtNames.ModelStructure,
        getOptions: function getOptions() {return _this9.viewer.config;} },

      {
        name: ExtNames.Hyperlinks,
        getOptions: function getOptions() {return {
            // Connect hyperlink handler
            loadViewableCb: function loadViewableCb(bubbleNode, numHyperlinks) {
              if (_this9.onHyperlink) {
                _this9.onHyperlink(bubbleNode, numHyperlinks);
              } else {
                // default-handler: Trigger view switch to linked sheet
                _this9.switchView([bubbleNode]);
              }
            } };} },


      {
        name: ExtNames.Minimap,
        getLoadingCondition: function getLoadingCondition() {return !_this9.options.disableMinimap;},
        getOptions: function getOptions() {return {
            // Allow clients to track minimap usage.
            // Todo: Finally, tracking should be consistently handled automatically inside LMV.
            trackUsage: _this9._trackMinimapUsage ? _this9._trackMinimapUsage.bind(_this9) : undefined };} },


      {
        name: ExtNames.Bookmarks,
        getLoadingCondition: function getLoadingCondition() {return _this9.is3D;},
        getOptions: function getOptions() {return {
            // Share global offset, so that bookmarks are placed correctly
            globalOffset: _this9.globalOffset,

            onBookmark: function onBookmark(bookmark, camera) {
              // Forward to handler to invoke view switch
              if (_this9.onBookmark) {
                _this9.onBookmark(bookmark, camera);
              } else {
                // default handler: Invoke view-switch to selected bookmark
                _this9.switchView([bookmark]);
              }

              // Activate BIMWalk for perspective views. It is important to do that
              // after invoking the handler to switch views.
              // Otherwise, BimWalk would be switched off again.
              if (camera.isPerspective) {
                _this9.startBimWalk();
              }
            },

            clusterfck: _this9.options.clusterfck,
            clusteringThreshold: 110 // threshold is (icon_width * 5), depends on "THREE.Vector3.distanceTo()"
          };} },

      {
        name: ExtNames.DropMe,
        getLoadingCondition: function getLoadingCondition() {return _this9.is3D === false;},
        getOptions: function getOptions() {return {
            enableGuidance: true,
            onDrop: _this9._handleDropMe.bind(_this9), // connect DropMe handler
            getTransformForNode: _this9._getTransformForNode.bind(_this9),
            getMain3DView: _this9._findMain3DView.bind(_this9) };} }];



    }

    // The extensions array contains objects with the following spec:
    //   name {string} - The extension's name to be loaded
    //   getLoadingCondition {function|optional} - under what conditions the extension should be loaded. If they are not
    //                                             met, the extension will be unloaded on _updateExtensions.
    //                                             Receives the AggregateView instance as parameter, and the bubble node.
    //   getOptions {function|optional} - Options to pass to the extension. Receives the AggregateView instance as parameter.
    //   onLoadedCB {function|optional} - Called when the extension's load promise was resolved. Receives the AggregateView instance as parameter,
    //                                    and the extension. If the promise is rejected, it passes the error instead.
    //   onBeforeUnloadCB {function|optional} - Called when the extension is about to be unloaded. Receives the AggregateView instance as parameter.
  }, { key: "_loadExtensions", value: function () {var _loadExtensions2 = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee4() {var _this10 = this;var loadingPromises;return regeneratorRuntime.wrap(function _callee4$(_context4) {while (1) {switch (_context4.prev = _context4.next) {case 0:
                this.extensions = this.options.extensions || this._getDefaultExtensions();

                loadingPromises = [];

                this.extensions.forEach(function (extension) {
                  if (!extension.getLoadingCondition || extension.getLoadingCondition(_this10)) {
                    loadingPromises.push(_this10._loadExtension(extension.name,
                    extension.getOptions && extension.getOptions(_this10),
                    extension.onLoadedCB));
                    _this10.extensionLoaded[extension.name] = true;
                  }
                });return _context4.abrupt("return",

                Promise.all(loadingPromises));case 4:case "end":return _context4.stop();}}}, _callee4, this);}));function _loadExtensions() {return _loadExtensions2.apply(this, arguments);}return _loadExtensions;}()


    // Load LMV extension with given options + optional options specified via this.options.extensionOptions
  }, { key: "_loadExtension", value: function () {var _loadExtension2 = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee5(extName, options, onLoadedCB) {var _this11 = this;var extOptions, customOptions, combinedOptions, promise;return regeneratorRuntime.wrap(function _callee5$(_context5) {while (1) {switch (_context5.prev = _context5.next) {case 0:
                extOptions = this.options.extensionOptions;
                customOptions = extOptions && extOptions[extName];
                combinedOptions = Object.assign({}, options, customOptions);
                promise = this.viewer.loadExtension(extName, combinedOptions);
                if (onLoadedCB) {
                  promise.then(function (extension) {
                    onLoadedCB(_this11, extension);
                  }).catch(function (error) {
                    // If extension loading failed, pass null to the callback, and the error as third parameter
                    onLoadedCB(_this11, null, error);
                  });
                }return _context5.abrupt("return",
                promise);case 6:case "end":return _context5.stop();}}}, _callee5, this);}));function _loadExtension(_x4, _x5, _x6) {return _loadExtension2.apply(this, arguments);}return _loadExtension;}()


    // Load/Unload extensions depending on whether we are entering a 2D or 3D view
  }, { key: "_updateExtensions", value: function _updateExtensions(bubbleNode) {var _this12 = this;
      var toggleExtension = function toggleExtension(extName, options, enable, onLoadedCB, onBeforeUnloadCB) {

        var extLoaded = _this12.extensionLoaded[extName];
        if (!extLoaded && enable) {
          _this12._loadExtension(extName, options, onLoadedCB).catch(function (err) {return _this12._onError(err);});
        } else if (extLoaded && !enable) {
          if (onBeforeUnloadCB) {
            onBeforeUnloadCB(_this12);
          }
          _this12.viewer.unloadExtension(extName);
        }
        _this12.extensionLoaded[extName] = enable;
      };

      this.extensions.forEach(function (extension) {
        if (extension.getLoadingCondition) {
          toggleExtension(extension.name,
          extension.getOptions && extension.getOptions(_this12),
          extension.getLoadingCondition(_this12, bubbleNode),
          extension.onLoadedCB,
          extension.onBeforeUnloadCB);
        }
      });
    } }, { key: "_initForFirstViewable", value: function _initForFirstViewable(

    bubbleNode) {

      // Check if we are about to switch between 2D and 3D view
      var dimChanged = this.is3D !== bubbleNode.is3D();
      if (dimChanged) {

        // Make sure that UI, camera, tools etc. are reset on next model-add.
        // This is only for convenience, so that the client doesn't have to care to call reset() from outside on 2D/3D switches.
        // If reset() was already called, we don't do it again, because this would
        // revert some settings (setCameraGlobal or startBimWalk) that the user might have done for this view already.
        if (!this.resetOnNextModelAdd) {
          this.reset();
          this.resetTriggeringBubble = bubbleNode;
        }

        this.is3D = bubbleNode.is3D();
      }

      // Make sure that the right extensions are loaded/unloaded
      this._updateExtensions(bubbleNode);

      // Before loading a 3D viewable, we must choose a refPoint for the view.
      this._updateRefPoint(bubbleNode);
    } }, { key: "_updateBookmarks", value: function _updateBookmarks()

    {
      var ext = this.viewer.getExtension(ExtNames.Bookmarks);
      if (!ext) {
        // Extension is only loaded in 3D mode
        return;
      }

      ext.resetBookmarks(this.bookmarks);
    }

    // Modify current camera based on the one specified in last setCameraGlobal call.
  }, { key: "_applyPendingCameraWhenReady", value: function _applyPendingCameraWhenReady() {

      // Applying the user camera requires the viewer camera to be default-initialized first.
      // This also implies that the viewer global offset is already determined.
      if (!this.cameraInitialized) {
        return;
      }

      // consume pending camera overrides
      var newCam = this.pendingCamera;
      this.pendingCamera = null;

      var cam = this.viewer.impl.camera;
      if (newCam.position) cam.position.copy(newCam.position);
      if (newCam.target) cam.target.copy(newCam.target);
      if (newCam.up) cam.up.copy(newCam.up);
      if (newCam.fov) cam.fov = newCam.fov;
      if (newCam.isPerspective !== undefined) cam.isPerspective = newCam.isPerspective;

      var globalOffset = this.globalOffset || this.viewer.model.getData().globalOffset;

      // GlobalOffset only affects 3D models
      if (this.is3D && !this.options.ignoreGlobalOffset) {
        cam.position.sub(globalOffset);
        cam.target.sub(globalOffset);
      }

      this.viewer.impl.syncCamera();
    }

    // Default handler: Purpose is to enable use of AggregatedView on its own and to test/demonstrate its usage.
    //
    // The DropMe defines the camera for the 3D view.
    // But: What 3D view is used for that may finally vary depending on application context.
    // For the default handler, we have no unique definition what 3D view we want to switch to. So, we have to use some heuristic choices.
  }, { key: "_findMain3DView", value: function _findMain3DView(sheetNode) {
      var masterViews = sheetNode.getMasterViews();

      if (masterViews[0]) {
        return masterViews[0];
      }

      var root = sheetNode === null || sheetNode === void 0 ? void 0 : sheetNode.getRootNode();

      if (!root) {
        return;
      }

      var views = root === null || root === void 0 ? void 0 : root.search(av.BubbleNode.MODEL_NODE);

      // Check if there is some view with Revit default name "{3D}"
      var isDefault3DView = function isDefault3DView(node) {return node.name() == "{3D}";};
      var default3DView = views.filter(isDefault3DView)[0];

      if (default3DView) {
        return default3DView;
      }

      // last fallback - just return first 3D view we find
      return views[0];
    } }, { key: "_handleDropMe", value: function _handleDropMe(

    pos, dir, mode, bubbleNode) {
      if (this.onDrop) {
        this.onDrop(pos, dir, mode, bubbleNode);
        return;
      }

      // No 3D view that we can switch to
      if (!bubbleNode) {
        console.warn("DropMe handler: Document does not contain a 3D view to switch to");
        return;
      }
      this.switchView(bubbleNode);

      // Setup perspective camera from DropMe input
      if (pos && dir) {
        var camera = {
          position: pos,
          target: pos.clone().add(dir),
          isPerspective: true // Note that DropMeTool usually produces "inside" views, which are not possible with ortho cameras
        };
        this.setCameraGlobal(camera);
      }
    }

    // Standard handler for view in 3D (available through the Drop me extension).
    // In the case this option was selected, first the dropMe callback is called without a position
    // (because it can only be found once the 3D model is loaded). Then this callback is called
    // with the found position.
  }, { key: "_handleViewIn3D", value: function _handleViewIn3D(position, target, isPosFrom2dTo3dMatrix, applySelectionCB) {
      var camera = {
        position: position,
        target: target,
        isPerspective: true };


      this.setCameraGlobal(camera);
      this.startBimWalk();
      applySelectionCB && applySelectionCB();
    }

    // All we want is to make sure that BIMWalk will actually start if startBimWalk was called - independent of timing.
  }, { key: "_startBimWalkWhenReady", value: function _startBimWalkWhenReady() {
      if (!this.bimWalkStartPending) {
        return;
      }

      var viewReady = !this.resetOnNextModelAdd; // tools/UI are ready for this view
      var bimWalk = this.viewer.getExtension(ExtNames.BimWalk);

      // Make sure that FusionOrbit doesn't switch BimWalk off again, because it resets NavTools on load
      var fusionOrbit = this.viewer.getExtension(ExtNames.FusionOrbit);
      if (viewReady && bimWalk && fusionOrbit) {
        bimWalk.activate();
        this.bimWalkStartPending = false;
      }
    } }, { key: "_unloadDiffTool", value: function _unloadDiffTool(

    clearCache) {
      var ext = this.viewer.getExtension(ExtNames.DiffTool);
      if (ext) {
        if (clearCache) {
          // discard cached diffs
          this.diffCache.length = 0;
        }

        this.viewer.unloadExtension(ExtNames.DiffTool);
      }
    }

    // currently used for load progress for 2d diff (as long as we need full geometry)
  }, { key: "_onProgressUpdate", value: function _onProgressUpdate(event) {

      if (!event.model || event.state !== av.ProgressState.LOADING) {
        return;
      }

      // debugging hint: use 'event.model.myData.basePath' as a key
      this.loadersInProgress[event.model.id] = event.percent;

      this._updateDiffLoadProgress();
    } }, { key: "_getDiffSupportModels", value: function _getDiffSupportModels()

    {
      var supp = this.diff.supportBubbles;
      if (!supp) {
        return;
      }
      return {
        diff: this.getModel(this.diff.supportBubbles.diff),
        primary: this.getModel(this.diff.supportBubbles.primary) };

    }

    // For 2D diff: Checks if the required support models for current diff mode are fully available.
    // If so, they are returned, otherwise it returns null.
  }, { key: "_diffSupportModelsReady", value: function _diffSupportModelsReady() {
      var supportModels = this._getDiffSupportModels();
      var diffLoaded = supportModels.diff && supportModels.diff.isLoadDone();
      var primaryLoaded = supportModels.primary && supportModels.primary.isLoadDone();
      return diffLoaded && primaryLoaded;
    }

    // If diff mode is active, all models that are not participating in the diff are
    // rendered in ghosted style.
  }, { key: "_updateGhosting", value: function _updateGhosting() {

      // keys of all models used in diff
      var usedInDiff = {};
      if (this.diff.enabled && this.diffBubbles) {
        usedInDiff = this.diffBubbles.forEach(function (b) {return usedInDiff[b.getModelKey()];});
      }

      for (var key in this.modelItems) {
        // skip models if root is not loaded yet
        var model = this.getModel(key);
        if (!model) {
          continue;
        }

        var ghosted = this.diff.enabled && !usedInDiff[key];
        var wasGhosted = !!this.modelIsGhosted[key];
        if (ghosted != wasGhosted) {
          model.setAllVisibility(!ghosted);

          // keep track which models are ghosted
          if (ghosted) {
            this.modelIsGhosted[key] = true;
          } else {
            delete this.modelIsGhosted[key];
          }
        }
      }
    }

    // Get array of models from given bubbleNode array.
    // NOTE: This will be removed, because the only difference to viewer.getVisibleModels() is that it may contain null-entries
    //       for models that are still loading. Note that _updateDiffLoadProgress() then needs some revision too, because it currently relies on these null-entries.
  }, { key: "getModels", value: function getModels(bubbleNodes) {var _this13 = this;
      return bubbleNodes.map(function (node) {return _this13.getModel(node);});
    }

    // Configures current modelTheming. 
    //  @param {Object|null} nodeToColor - maps model keys to theming colors. Use node.getModelKey() to get key of a BubbleNode.
  }, { key: "setModelTheming", value: function setModelTheming(nodeToColor) {

      // accept null/undefined to clear model theming
      nodeToColor = nodeToColor || {};

      var oldStates = this.modelThemingStates;
      this.modelThemingStates = {};

      // update theming states for all models to be colored
      for (var key in nodeToColor) {
        var state = oldStates[key];
        var newColor = nodeToColor[key];
        var model = this.getModel(key);

        // Create new state or update existing one
        if (!state) {
          state = new ModelThemingState(newColor);
        } else {
          // set new color
          var changed = !state.color.equals(newColor);
          if (changed) {
            // apply new color
            state.color.copy(newColor);

            // If the old color was active, make sure we apply the new one
            // when we update below.
            state.active = false;
          }
        }

        // Store new state
        this.modelThemingStates[key] = state;

        // make sure colors are applied as specified
        model && this._updateModelTheming(model);
      }

      // Make sure that we don't leak any outdated theming colors
      for (var _key in oldStates) {
        var _state = oldStates[_key];
        var removed = !nodeToColor[_key];
        var _model = this.getModel(_key);
        if (removed && _state.active) {
          _model && this.viewer.clearThemingColors(_model);
        }
      }
    } }, { key: "_signalDiffLoadProgress", value: function _signalDiffLoadProgress(

    percent, force) {
      if (force || percent !== this._lastDiffLoadPercent) {
        // update progress bar
        var msg = av.i18n.t('Loading Model for Change Visualization');
        this.diff.progressCallback(percent, msg);

        this._lastDiffLoadPercent = percent;
      }
    } }, { key: "_updateDiffLoadProgress", value: function _updateDiffLoadProgress()

    {var _this14 = this;
      // Do nothing if we are not waiting for a diff
      if (!this.diffNeedsUpdate || !this.diff.enabled || this.diff.empty || !this.diff.progressCallback) {
        return;
      }

      // get array of all models
      var diffModels = this.getModels(this.diff.diffBubbles);
      var primModels = this.getModels(this.diff.primaryBubbles);
      var allModels = diffModels.concat(primModels);

      // for 2d, also consider support models
      var supportModels = this._getDiffSupportModels();
      if (supportModels) {
        allModels.push(
        supportModels.diff,
        supportModels.primary);

      }

      // check how many models roots need to be loaded
      var loadedModels = allModels.filter(Boolean).length;

      // Reserve the first 10% progress bar for loading model roots
      var ModelRootPercent = 10;

      // As long as not all roots are loaded, just track model-root loading
      if (loadedModels < allModels.length) {
        var _percent = Math.floor(ModelRootPercent * loadedModels / allModels.length);
        this._signalDiffLoadProgress(_percent);
        return;
      }

      // For 2D diff, we track the model-load progress including geometry
      // This can be changed as soon as we eliminate geom loading from 2d too.
      if (!this.is3D) {
        // sum up progress percent of all models we need
        var countPercentDone = function countPercentDone(acc, model) {return acc + (_this14.loadersInProgress[model.id] | 0);};
        var geomPercentDone = allModels.reduce(countPercentDone, 0);
        var geomPercentTotal = allModels.length * 100;

        // map average loader percent to the upper 90%, because we had reserved some percentage
        // for the model root loading already.
        var _percent2 = ModelRootPercent + Math.floor((100 - ModelRootPercent) * geomPercentDone / geomPercentTotal);
        this._signalDiffLoadProgress(_percent2);

        return;
      }

      // Track fragment list loading
      var countLoadedFrags = function countLoadedFrags(acc, model) {return acc + model.getData().fragsLoadedNoGeom;};
      var countFragsTotal = function countFragsTotal(acc, model) {return acc + model.getData().metadata.stats.num_fragments;};
      var fragsLoaded = allModels.reduce(countLoadedFrags, 0);
      var fragsTotal = allModels.reduce(countFragsTotal, 0);

      var percent = ModelRootPercent + Math.floor((100 - ModelRootPercent) * fragsLoaded / fragsTotal);
      this._signalDiffLoadProgress(percent);
    } }, { key: "_updateDiff", value: function _updateDiff()

    {

      if (!this.diffNeedsUpdate) {
        return;
      }

      this._updateGhosting();

      this._updateDiffLoadProgress();

      if (!this.diff.enabled) {
        // Unload extension if needed on DiffMode disabling
        this._unloadDiffTool();
        this._updateAllModelTheming(); // reactive modelTheming
        return;
      }

      // collect all models participating in the diff
      var diffModels = this.getModels(this.diff.diffBubbles);
      var primModels = this.getModels(this.diff.primaryBubbles);
      for (var i = 0; i < diffModels.length; i++) {
        var diffModel = diffModels[i];
        var primModel = primModels[i];

        // If loading is in progress, modelA or modelB might just be a boolean value
        var diffModelLoaded = diffModel instanceof av.Model && (diffModel.isOTG() || diffModel.isLoadDone());
        var primModelLoaded = primModel instanceof av.Model && (primModel.isOTG() || primModel.isLoadDone());
        if (!diffModelLoaded || !primModelLoaded) {
          // We cannot start diff until all models are loaded (for 2D, we also need geometry)
          return;
        }

        // Make sure that the property db is loaded. Otherwise, we will retry on next
        // MODEL_ROOT_LOADED event.
        if (!diffModel.getPropertyDb() || !primModel.getPropertyDb()) {
          return;
        }
      }

      // If we need support models (for 2D diff), make sure that these are reday too
      var supportModels = undefined;
      var supportModelsNeeded = !!this.diff.supportBubbles;
      if (supportModelsNeeded) {

        if (!this._diffSupportModelsReady()) {
          // We cannot start diff before support models (incl. geometry)
          // are fully available. So stop here and wait until called again by
          // next geometry-loaded event.
          return;
        }
        supportModels = this._getDiffSupportModels();
      }

      var elapsed = performance.now() - this.diffStartTime;
      console.log('Time for loading diff models: ', elapsed);

      // Make sure that all model-theming is reverted before activating DiffToolExtension
      this._updateAllModelTheming();

      this._setDiffModels(diffModels, primModels, supportModels);
    } }, { key: "_setDiffModels", value: function _setDiffModels(

    diffModels, primaryModels, supportModels) {var _this15 = this;

      // don't reset diff again until next setModels() call
      this.diffNeedsUpdate = false;

      // If we are in DiffMode, but no model version changed, we just show
      // all in ghosted and are done.
      if (!primaryModels.length) {
        this._unloadDiffTool();
        return;
      }

      var ext = this.viewer.getExtension(ExtNames.DiffTool);
      if (ext) {
        ext.replaceModels(diffModels, primaryModels, supportModels);
      } else {

        var cfg = {
          diffModels: diffModels,
          primaryModels: primaryModels,
          supportModels: supportModels, // = { primary: av.Model, diff: av.Model }
          diffadp: false,
          availableDiffModes: ['overlay', 'sidebyside'],
          diffMode: 'overlay',

          versionA: this.diff.primaryBubbleLabel,
          versionB: this.diff.diffBubbleLabel,
          refNames: this.diff.refNames,
          mimeType: 'application/vnd.autodesk.revit', // change to "C4R or Revit"
          hotReload: true,
          diffCache: this.diffCache,

          // To run with OTG, we need to use the new code path for side-by-side that uses a single viewer instance.
          useSplitScreenExtension: true,

          // Add a section with details, such as which versions are compared.
          // If activated, the overlay displaying the version labels is not
          // rendered as that information is already contained in the panel.
          // This option is disabled by default for backwards compatibility.
          showDetailsSection: true,

          progress: function progress(percent, state) {

            // Diff progress might be null if diffConfig has changed already. This may happen when clicking ('Exit Changes')
            // while waiting for the diff.
            if (!_this15.diff.progressCallback) {
              return;
            }

            if (state === Autodesk.DiffTool.DIFFTOOL_PROGRESS_STATES.LoadingPropDb) {
              _this15.diff.progressCallback(percent, av.i18n.t('Loading element properties'));
              return;
            }

            if (percent === 100 && _this15.onDiffDone) {
              _this15.onDiffDone();
            }
            _this15.diff.progressCallback(percent, av.i18n.t('Computing change visualization'));
          },
          // Exclude objects based on selected level
          excludeFromDiff: function excludeFromDiff(model, dbId) {
            return _this15.levelsExtension && !_this15.levelsExtension.floorSelector.isVisible(model, dbId);
          },
          // Whenever the DiffTools sets dbIds to visible for a model,
          // we rerun the current FloorSelectionFilter to make sure that
          // an object is only shown if both - FloorSelectionFilter and
          // DiffTool allow it.
          setNodesOff: function setNodesOff(model) {
            var levelExt = _this15.levelsExtension;
            if (levelExt) {
              levelExt.floorSelector._floorSelectorFilter.reApplyFilter(model);
            }
          },
          hideModeSwitchButton: true };


        this._loadExtension(ExtNames.DiffTool, cfg).catch(function (err) {return _this15._onError(err);});
      }
    }

    // Make sure that modelTheming colors are applied/cleared according to current modelTheming state
  }, { key: "_updateModelTheming", value: function _updateModelTheming(model) {
      var key = makeKey(model);
      var state = this.modelThemingStates[key];

      // If there is no state, the model was not affected by modelTheming and leave theming as it is
      if (!state) {
        return;
      }

      // Suppress all diff as long as diff is active
      var diffExt = this.viewer.getExtension(ExtNames.DiffTool);
      var enableTheming = !diffExt;
      state.setEnabled(model, enableTheming);

      // make sure that theming is applied if enabled
      state.update(model);

      this.viewer.impl.invalidate(true);
    } }, { key: "_updateAllModelTheming", value: function _updateAllModelTheming()

    {
      for (var key in this.modelItems) {
        var model = this.getModel(key);
        model && this._updateModelTheming(model);
      }
    } }, { key: "getAlignmentService", value: function () {var _getAlignmentService = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee6() {return regeneratorRuntime.wrap(function _callee6$(_context6) {while (1) {switch (_context6.prev = _context6.next) {case 0:if (!



                this.alignmentService) {_context6.next = 2;break;}return _context6.abrupt("return",
                this.alignmentService);case 2:if (!




                this._alignmentServicePromise) {_context6.next = 7;break;}_context6.next = 5;return (
                  this._alignmentServicePromise);case 5:_context6.next = 11;break;case 7:

                // Init alignment service at first use
                this._alignmentServicePromise = this._initAlignmentService();_context6.next = 10;return (
                  this._alignmentServicePromise);case 10:
                this._alignmentServicePromise = null;case 11:return _context6.abrupt("return",


                this.alignmentService);case 12:case "end":return _context6.stop();}}}, _callee6, this);}));function getAlignmentService() {return _getAlignmentService.apply(this, arguments);}return getAlignmentService;}()


    // Check if we have to care for alignment services.
  }, { key: "isUsingAlignmentService", value: function isUsingAlignmentService() {
      // Note that alignmentService may also be specified in the options,
      // but the createAlignmentService() call might not be finished yet.
      return this.options.createModelAlignmentService || this.alignmentService;
    } }, { key: "_initAlignmentService", value: function () {var _initAlignmentService2 = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee8() {var _this16 = this;var createService, initService;return regeneratorRuntime.wrap(function _callee8$(_context8) {while (1) {switch (_context8.prev = _context8.next) {case 0:


                // Stop here if service is not needed
                createService = this.options.createModelAlignmentService;if (
                createService) {_context8.next = 3;break;}return _context8.abrupt("return");case 3:



                // Set alignmentService when ready.
                initService = /*#__PURE__*/function () {var _ref4 = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee7() {var service;return regeneratorRuntime.wrap(function _callee7$(_context7) {while (1) {switch (_context7.prev = _context7.next) {case 0:_context7.next = 2;return (
                              createService());case 2:service = _context7.sent;
                            _this16.setAlignmentService(service);case 4:case "end":return _context7.stop();}}}, _callee7);}));return function initService() {return _ref4.apply(this, arguments);};}();


                // Load AlignmentService extension
                _context8.next = 6;return this.viewer.loadExtension('Autodesk.ModelAlignmentService').then(initService);case 6:case "end":return _context8.stop();}}}, _callee8, this);}));function _initAlignmentService() {return _initAlignmentService2.apply(this, arguments);}return _initAlignmentService;}() }, { key: "_getTransformForNode",


    // Use alignment service to load a transform matrix for a given node.
    value: function () {var _getTransformForNode2 = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee9(bubbleNode) {var alignmentService, versionUrn, viewableName, matrix;return regeneratorRuntime.wrap(function _callee9$(_context9) {while (1) {switch (_context9.prev = _context9.next) {case 0:_context9.next = 2;return (
                  this.getAlignmentService());case 2:alignmentService = _context9.sent;if (

                alignmentService) {_context9.next = 5;break;}return _context9.abrupt("return");case 5:_context9.next = 7;return (




                  Promise.all(this.pendingAlignmentFetches));case 7:

                // get base64-encoded versionUrn
                versionUrn = bubbleNode.getRootNode().urn();
                viewableName = bubbleNode.is2D() ? av.toUrlSafeBase64(bubbleNode.name()) : undefined;

                // get override transform (may be null)
                _context9.next = 11;return alignmentService.loadTransform(versionUrn, viewableName);case 11:matrix = _context9.sent;return _context9.abrupt("return",

                matrix);case 13:case "end":return _context9.stop();}}}, _callee9, this);}));function _getTransformForNode(_x7) {return _getTransformForNode2.apply(this, arguments);}return _getTransformForNode;}()


    // If an alignment service is set and specifies a transform for this model,
    // the loadOptions are modified to replace refPointTransform by the alignment matrix specified by the service. 
    //  @param {BubbleNode}  bubbleNode  - documentNode to be loaded
    //  @param {loadOptions} loadOptions - modified in-place if an override alignment transform is specified
  }, { key: "_applyAlignmentService", value: function () {var _applyAlignmentService2 = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee10(bubbleNode, loadOptions) {var matrix;return regeneratorRuntime.wrap(function _callee10$(_context10) {while (1) {switch (_context10.prev = _context10.next) {case 0:_context10.next = 2;return (

                  this._getTransformForNode(bubbleNode));case 2:matrix = _context10.sent;

                // If there is an override transform... 
                if (matrix) {
                  // use this transform instead of the source-file's refPointTransform
                  loadOptions.applyRefPoint = false;
                  loadOptions.placementTransform = matrix;

                  // Important if the client uses unit scaling (otherwise it has no effect):
                  //
                  // If unitScaling is used, LMV treats refPointTransforms and loadOptions.placemenTransform differently by default:
                  //  - For refPointTransforms, the application order is:             1. refPointTransform, 2. unitScaling
                  //  - For loadOptions.placementTransform, the application order is: 1. scaling,           2. placementTransform
                  //
                  // In our case, the alignmentTransform is supposed to replace the refPointTransform. So, it needs to be applied _before_ the scaling,
                  // so that it would be handled in the same way as the refPointTransform would be. This is achieved by this flag,
                  // which flips the application order of placementTransform and unit scaling.
                  loadOptions.applyPlacementInModelUnits = true;
                }case 4:case "end":return _context10.stop();}}}, _callee10, this);}));function _applyAlignmentService(_x8, _x9) {return _applyAlignmentService2.apply(this, arguments);}return _applyAlignmentService;}() }], [{ key: "findDiffSupportModel", value: function findDiffSupportModel(sheetNode) {// Support model is needed only for 2D sheet nodes.
      if (!sheetNode.is2D()) {return null;} // get phase name (or first one if there are actually multiple ones)
      var phases = sheetNode.data.phaseNames;var phase = Array.isArray(phases) ? phases[0] : phases;if (!phase) {console.warn("A sheet node must have a phase name. Missing for sheet: ".concat(sheetNode.name));return null;}return sheetNode.getMasterView(phase);} // find corresponding 3D model for each diff/primary
  }, { key: "findDiffSupportModels", value: function findDiffSupportModels(diffConfig) {// Note that 2D diff is never aggregated.
      diffConfig.supportBubbles.diff = AggregatedView.findDiffSupportModel(diffConfig.diffBubbles[0]);diffConfig.supportBubbles.primary = AggregatedView.findDiffSupportModel(diffConfig.primaryBubbles[0]);} }]);return AggregatedView;}();AggregatedView.ExtNames = ExtNames;
AggregatedView.AlignmentServices = AlignmentServices;